import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Palette,
  Save,
  RestartAlt,
  Star,
  Title,
  CropLandscape,
  ViewCarousel,
  Favorite,
  TextFields,
  AccessTime,
  ShieldOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { GlassmorphicCard, PrimaryButton, SecondaryButton } from '../styles/sharedStyles';

const Toggle = ({ settings, keyName, label, icon, onChange }) => {
  const enabled = Boolean(settings[keyName]);
  return (
    <FormControlLabel
      control={
        <Switch
          checked={enabled}
          onChange={(e) => onChange(keyName, e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase': {
              color: enabled ? settings.primary_color : 'rgba(255,255,255,0.5)',
            },
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: settings.primary_color,
            },
            '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-track': {
              backgroundColor: settings.primary_color,
            },
          }}
        />
      }
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {icon}
          <span style={{ color: enabled ? settings.primary_color : 'rgba(255,255,255,0.7)', fontWeight: enabled ? 600 : 400 }}>{label}</span>
        </Box>
      }
      sx={{ width: '100%', margin: 0 }}
    />
  );
};

const EFFECT_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'pulse', label: 'Pulse (soft glow)' },
  { value: 'typewriter', label: 'Typewriter' },
  { value: 'bounce', label: 'Bounce In' },
  { value: 'glitch', label: 'Glitch (⚠️ can cause discomfort)' },
];

const TEXT_FIELDS = [
  { key: 'login_welcome_text', label: 'Welcome Text', placeholder: 'e.g. Welcome to WatchTower', icon: <Star sx={{ fontSize: 18 }} /> },
  { key: 'login_whats_new_text', label: 'What\'s New Text', placeholder: 'e.g. 🚀 New features: 2FA, Glassmorphism, Live stats', icon: <TextFields sx={{ fontSize: 18 }} /> },
];

const LogonPageSettings = ({ apiBaseUrl, token }) => {
  const [settings, setGuiSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBaseUrl}/api/gui/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuiSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch GUI settings', err);
      setError('Failed to load logon page settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = useCallback((key, value) => {
    setGuiSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleTextChange = useCallback((key, value) => {
    setGuiSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await axios.post(`${apiBaseUrl}/api/gui/settings`, { ...settings }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      try { localStorage.setItem('theme_settings', JSON.stringify(settings)); } catch (e) {}
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      window.location.reload();
    } catch (err) {
      console.error('Failed to save GUI settings', err);
      setError('Failed to save logon page settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaults = {
      login_welcome_enabled: true,
      login_welcome_text: 'Welcome to WatchTower',
      login_welcome_effect: 'pulse',
      login_glitch_title: true,
      login_title_effect: 'pulse',
      login_hologram_border: true,
      login_3d_tilt: true,
      login_clock_enabled: true,
      login_whats_new_enabled: true,
      login_whats_new_text: '🚀 New features: 2FA login, Glassmorphism UI, Live stats dashboard',
      login_icons_in_fields: true,
      login_ip_check_banner: true,
    };
    setGuiSettings(prev => ({ ...prev, ...defaults }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: settings.primary_color }} />
      </Box>
    );
  }

  const primaryColor = settings.primary_color || '#00d4ff';

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Logon page settings applied successfully! Refreshing...
        </Alert>
      )}

      <GlassmorphicCard sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ color: primaryColor, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Palette /> Logon Page Gadgets
        </Typography>

        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: 'rgba(0, 212, 255, 0.04)',
            border: '1px solid rgba(0, 212, 255, 0.15)',
            borderRadius: '16px',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star sx={{ fontSize: 20 }} /> Visual Effects (on login card)
          </Typography>
          <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(0, 212, 255, 0.08)', border: '1px solid rgba(0, 212, 255, 0.3)', color: '#00d4ff', '& .MuiAlert-icon': { color: '#00d4ff' } }}>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              These effects apply to the login page shown before authentication. Changes take effect on page reload.
            </Typography>
          </Alert>

          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Toggle
                settings={settings}
                keyName="login_welcome_enabled"
                label="Animated Welcome Text"
                icon={<Star sx={{ fontSize: 18 }} />}
                onChange={handleToggle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: 'rgba(255,255,255,0.65)' }}>
                  Welcome Effect
                </InputLabel>
                <Select
                  value={settings.login_welcome_effect || 'pulse'}
                  label="Welcome Effect"
                  onChange={(e) => handleTextChange('login_welcome_effect', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                      '&.Mui-focused fieldset': { borderColor: settings.primary_color || '#00d4ff' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.65)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: settings.primary_color || '#00d4ff' },
                    '& .MuiSelect-select': { color: 'white' },
                  }}
                >
                  {EFFECT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Toggle
                settings={settings}
                keyName="login_glitch_title"
                label="Show Title Text"
                icon={<Title sx={{ fontSize: 18 }} />}
                onChange={handleToggle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: 'rgba(255,255,255,0.65)' }}>
                  Title Effect
                </InputLabel>
                <Select
                  value={settings.login_title_effect || 'pulse'}
                  label="Title Effect"
                  onChange={(e) => handleTextChange('login_title_effect', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                      '&.Mui-focused fieldset': { borderColor: settings.primary_color || '#00d4ff' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.65)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: settings.primary_color || '#00d4ff' },
                    '& .MuiSelect-select': { color: 'white' },
                  }}
                >
                  {EFFECT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Toggle
                settings={settings}
                keyName="login_hologram_border"
                label="Hologram Rotating Border"
                icon={<CropLandscape sx={{ fontSize: 18 }} />}
                onChange={handleToggle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Toggle
                settings={settings}
                keyName="login_3d_tilt"
                label="3D Card Tilt on Hover"
                icon={<ViewCarousel sx={{ fontSize: 18 }} />}
                onChange={handleToggle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Toggle
                settings={settings}
                keyName="login_clock_enabled"
                label="Live Digital Clock"
                icon={<AccessTime sx={{ fontSize: 18 }} />}
                onChange={handleToggle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Toggle
                settings={settings}
                keyName="login_whats_new_enabled"
                label="What's New Tooltip"
                icon={<Favorite sx={{ fontSize: 18 }} />}
                onChange={handleToggle}
              />
            </Grid>
             <Grid item xs={12} sm={6}>
              <Toggle
                settings={settings}
                keyName="login_icons_in_fields"
                label="Icons in Input Fields"
                icon={<TextFields sx={{ fontSize: 18 }} />}
                onChange={handleToggle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Toggle
                settings={settings}
                keyName="login_ip_check_banner"
                label="IP Check Banner"
                icon={<ShieldOutlined sx={{ fontSize: 18 }} />}
                onChange={handleToggle}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper
          sx={{
            p: 3,
            backgroundColor: 'rgba(0, 212, 255, 0.04)',
            border: '1px solid rgba(0, 212, 255, 0.15)',
            borderRadius: '16px',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextFields sx={{ fontSize: 20 }} /> Custom Text
          </Typography>
          <Grid container spacing={3}>
            {TEXT_FIELDS.map((field) => (
              <Grid item xs={12} key={field.key}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  {field.icon} {field.label}
                </Typography>
                <TextField
                  fullWidth
                  multiline={field.key === 'login_whats_new_text'}
                  rows={field.key === 'login_whats_new_text' ? 3 : 1}
                  value={settings[field.key] || ''}
                  onChange={(e) => handleTextChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                      '&.Mui-focused fieldset': { borderColor: primaryColor },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.65)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
                    '& .MuiOutlinedInput-input': { color: 'white' },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Grid container sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
          <SecondaryButton onClick={handleReset} startIcon={<RestartAlt />}>
            Reset to Defaults
          </SecondaryButton>
          <PrimaryButton
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          >
            {saving ? 'Saving...' : 'Apply Logon Settings'}
          </PrimaryButton>
        </Grid>
      </GlassmorphicCard>
    </Box>
  );
};

export default LogonPageSettings;